// =============================================================================
// useLogs Hook
// =============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import {
  ApiClientError,
  type LogEntry,
  type SearchParams,
  type LogLevel,
} from '../lib/api.js';

// =============================================================================
// Types
// =============================================================================

export interface UseLogsOptions {
  /** Base URL override */
  baseUrl?: string;
  /** Initial search parameters */
  initialParams?: Partial<SearchParams>;
  /** Initial app ID for fetchOnMount */
  initialAppId?: string;
  /** Whether to fetch logs on mount (default: false) */
  fetchOnMount?: boolean;
  /** Auto-refresh interval in milliseconds (0 = disabled) */
  refreshInterval?: number;
}

export interface UseLogsResult {
  /** List of log entries */
  logs: LogEntry[];
  /** Total number of matching logs */
  total: number;
  /** Whether there are more logs to load */
  hasMore: boolean;
  /** Whether logs are being loaded */
  isLoading: boolean;
  /** Error from the last operation */
  error: string | null;
  /** Current search parameters */
  params: SearchParams | null;
  /** Current app ID being searched */
  currentAppId: string | null;
  /** Search logs with parameters for a specific app */
  search: (params: SearchParams, appId: string) => Promise<void>;
  /** Load next page of results */
  loadMore: () => Promise<void>;
  /** Refresh current search */
  refresh: () => Promise<void>;
  /** Clear logs and reset state */
  clear: () => void;
  /** Clear error state */
  clearError: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Create default search params for the last hour.
 */
function createDefaultParams(): SearchParams {
  const now = new Date();
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

  return {
    from: oneHourAgo.toISOString(),
    to: now.toISOString(),
    limit: 100,
    offset: 0,
  };
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for searching and paginating logs.
 *
 * @example
 * ```tsx
 * function LogViewer() {
 *   const {
 *     logs,
 *     total,
 *     hasMore,
 *     isLoading,
 *     error,
 *     search,
 *     loadMore,
 *     refresh,
 *   } = useLogs();
 *
 *   // Search last hour of error logs
 *   const searchErrors = () => {
 *     const now = new Date();
 *     const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
 *     search({
 *       from: oneHourAgo.toISOString(),
 *       to: now.toISOString(),
 *       levels: ['error', 'fatal'],
 *       limit: 50,
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={searchErrors}>Search Errors</button>
 *       <button onClick={refresh} disabled={isLoading}>Refresh</button>
 *       {error && <div className="error">{error}</div>}
 *       <p>Showing {logs.length} of {total} logs</p>
 *       <ul>
 *         {logs.map(log => (
 *           <li key={log.id}>[{log.level}] {log.message}</li>
 *         ))}
 *       </ul>
 *       {hasMore && (
 *         <button onClick={loadMore} disabled={isLoading}>Load More</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useLogs(options: UseLogsOptions = {}): UseLogsResult {
  const { baseUrl, initialParams, initialAppId, fetchOnMount = false, refreshInterval = 0 } = options;
  const { client } = useLogCollectorClient({ baseUrl });

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<SearchParams | null>(
    initialParams ? { ...createDefaultParams(), ...initialParams } : null
  );
  const [currentAppId, setCurrentAppId] = useState<string | null>(null);

  // Track if component is mounted for async operations
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Search logs with parameters.
   */
  const search = useCallback(
    async (searchParams: SearchParams, appId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      // Ensure offset starts at 0 for new searches
      const normalizedParams: SearchParams = {
        ...searchParams,
        offset: searchParams.offset ?? 0,
        limit: searchParams.limit ?? 100,
      };

      setParams(normalizedParams);
      setCurrentAppId(appId);

      try {
        const result = await client.searchLogs(normalizedParams, appId);

        if (!mountedRef.current) return;

        setLogs(result.logs);
        setTotal(result.total);
        setHasMore(result.has_more);
      } catch (err) {
        if (!mountedRef.current) return;

        if (err instanceof ApiClientError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to search logs');
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [client]
  );

  /**
   * Load next page of results.
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!params || !currentAppId || isLoading || !hasMore) return;

    setIsLoading(true);
    setError(null);

    const nextParams: SearchParams = {
      ...params,
      offset: (params.offset ?? 0) + (params.limit ?? 100),
    };

    setParams(nextParams);

    try {
      const result = await client.searchLogs(nextParams, currentAppId);

      if (!mountedRef.current) return;

      setLogs((prev) => [...prev, ...result.logs]);
      setTotal(result.total);
      setHasMore(result.has_more);
    } catch (err) {
      if (!mountedRef.current) return;

      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load more logs');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [client, params, currentAppId, isLoading, hasMore]);

  /**
   * Refresh current search.
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (!params || !currentAppId) return;

    // Reset to first page
    const refreshParams: SearchParams = {
      ...params,
      offset: 0,
    };

    await search(refreshParams, currentAppId);
  }, [params, currentAppId, search]);

  /**
   * Clear logs and reset state.
   */
  const clear = useCallback((): void => {
    setLogs([]);
    setTotal(0);
    setHasMore(false);
    setParams(null);
    setCurrentAppId(null);
    setError(null);
  }, []);

  /**
   * Clear error state.
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount && initialParams && initialAppId) {
      search({ ...createDefaultParams(), ...initialParams }, initialAppId);
    }
  }, [fetchOnMount, initialParams, initialAppId, search]);

  // Auto-refresh if interval is set
  useEffect(() => {
    if (refreshInterval <= 0 || !params) return;

    const intervalId = setInterval(() => {
      refresh();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [refreshInterval, params, refresh]);

  return {
    logs,
    total,
    hasMore,
    isLoading,
    error,
    params,
    currentAppId,
    search,
    loadMore,
    refresh,
    clear,
    clearError,
  };
}
